import Image from "next/image";
import { displayFont } from "@/components/content-blocks/band-helpers";
import type { MediaAsset } from "@/lib/storefront-types";

type ContentPageIntroProps = {
  title: string;
  description?: string | null;
  image?: MediaAsset | null;
  showFallbackCard?: boolean;
};

export function ContentPageIntro({
  title,
  description,
  image,
  showFallbackCard = true,
}: ContentPageIntroProps) {
  const hasImage = Boolean(image?.url);
  const showMediaCard = hasImage || showFallbackCard;

  return (
    <section className="storefront-page-intro-background relative overflow-hidden border-y storefront-border-soft">
      <div className="storefront-soft-radial-top-right pointer-events-none absolute inset-0" />

      <div
        className={`relative mx-auto grid max-w-6xl gap-6 px-4 py-7 sm:px-6 sm:py-8 lg:items-center lg:gap-10 lg:py-10 ${
          showMediaCard
            ? "lg:grid-cols-[minmax(0,1.3fr)_minmax(16rem,20rem)]"
            : ""
        }`}
      >
        <div className="space-y-4 text-center lg:text-left">
          <div className="space-y-3">
            <h1
              className={`${displayFont.className} text-[clamp(1.85rem,3.6vw,3.2rem)] font-bold leading-[1.06] tracking-tight storefront-text-heading`}
            >
              {title}
            </h1>
            {description ? (
              <p className="mx-auto max-w-[42rem] text-[0.98rem] leading-7 storefront-text-body sm:text-[1.05rem] lg:mx-0">
                {description}
              </p>
            ) : null}
          </div>
        </div>

        {showMediaCard ? (
          <div className="hidden lg:block lg:justify-self-end">
            <div className="relative overflow-hidden rounded-[1.6rem] border border-white/80 bg-white/92 shadow-[0_24px_48px_-32px_rgba(0,39,0,0.24)]">
              <div className="storefront-soft-divider-horizontal absolute inset-x-5 top-0 h-px" />
              <div className="flex min-h-[12.5rem] w-full min-w-[15.5rem] items-center justify-center bg-white p-4 sm:min-h-[14rem] sm:min-w-[17.5rem] sm:p-5">
                {hasImage ? (
                  <div className="relative h-[10rem] w-full sm:h-[11rem]">
                    <Image
                      src={image!.url}
                      alt={image!.alt || `${title} header image`}
                      fill
                      sizes="(min-width: 1024px) 18rem, (min-width: 640px) 40vw, 80vw"
                      className="object-contain"
                    />
                  </div>
                ) : (
                  <div className="space-y-2 text-center">
                    <p className="text-2xl font-semibold tracking-tight storefront-text-heading">
                      {title}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
